import { createContext, FocusEvent, useContext, useMemo } from 'react';

import { createForm } from '@components/form';
import { useFormField } from '@components/form/use-form-field';
import { SelectMulti } from '@components/selectmulti';
import { TextArea } from '@components/textarea';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { FieldMinHeightWrapper } from './common';
import type {
  IntegrationEndpoint,
  IntegrationEndpointArrayOfStringsVariable,
  IntegrationEndpointListVariable,
  IntegrationEndpointSelectVariable,
  IntegrationEndpointVariable,
} from './integrations-types';
import { useDynamicEndpointOptions } from './lists/use-dynamic-endpoint-options';

type Data = {};

const Form = createForm<Data>();

const idSeparator = '---';

const getFieldId = (name: string, field: string): keyof Data => {
  return `${name}${idSeparator}${field}` as keyof Data;
};

export const getFieldIds = (name: string) => {
  return {
    typeId: getFieldId(name, 'type'),
    optionsFromId: getFieldId(name, 'options-from'),
    valueTypeId: getFieldId(name, 'value-type'),
    labelId: getFieldId(name, 'label'),
    exampleId: getFieldId(name, 'example'),
    defaultValueId: getFieldId(name, 'default'),
    visibleId: getFieldId(name, 'visible'),
    requiredId: getFieldId(name, 'required'),
    formatId: getFieldId(name, 'format'),
    jqQueryId: getFieldId(name, 'jqQuery'),
  };
};

export const getParameterMapFieldId = (variableName: string, parameterName: string) =>
  `${variableName}${idSeparator}parameter-map${idSeparator}${parameterName}`;

export const parseParameterMapFieldId = (fieldId: string) => {
  const parts = fieldId.split(idSeparator);
  if (parts.length !== 3 || parts[1] !== 'parameter-map') return null;
  return { variableName: parts[0], parameterName: parts[2] };
};

type EndpointType = IntegrationEndpoint<string> | IntegrationEndpoint;
export const endpointContext = createContext<EndpointType>({} as EndpointType);

type ValueFieldProps = {
  field: IntegrationEndpointVariable;
  form: typeof Form;
  id: keyof Data;
  defaultValue: any;
};

export const ExampleValueField = ({ field, form: Form, id, defaultValue }: ValueFieldProps) => {
  return (
    <ValueField id={id} field={field} form={Form} defaultValue={defaultValue} type="example" />
  );
};

type FileFieldProps = {
  field: Pick<IntegrationEndpointVariable, 'name' | 'example' | 'type'>;
  form: typeof Form;
  id: keyof Data;
};

const MultipartExampleFileField = ({ field, form: Form, id }: FileFieldProps) => {
  const endpoint = useContext(endpointContext);
  const exampleFile = endpoint.exampleFiles?.find((file) => file.variableName === field.name);
  const file = exampleFile
    ? { id: exampleFile.id, name: exampleFile.name, src: exampleFile.src }
    : undefined;
  const url = file ? new URL(file.src) : undefined;
  const key = `${url ? url.pathname.split('/').pop() : undefined}`;
  return (
    <Form.FileField
      id={id}
      key={key}
      label={field.name}
      hideLabel
      variant="small"
      defaultValue={file}
    />
  );
};

const MultipartDefaultFileField = ({ field, form: Form, id }: FileFieldProps) => {
  const endpoint = useContext(endpointContext);
  const defaultFile = endpoint.defaultFiles?.find((file) => file.variableName === field.name);
  const file = defaultFile
    ? { id: defaultFile.id, name: defaultFile.name, src: defaultFile.src }
    : undefined;
  const url = file ? new URL(file.src) : undefined;
  const key = `${url ? url.pathname.split('/').pop() : undefined}`;
  return (
    <Form.FileField
      id={id}
      key={key}
      label={field.name}
      hideLabel
      variant="small"
      defaultValue={file}
    />
  );
};

export const DefaultValueField = ({ field, form: Form, id, defaultValue }: ValueFieldProps) => {
  return (
    <ValueField id={id} field={field} form={Form} defaultValue={defaultValue} type="default" />
  );
};

const ValueField = ({
  field,
  form: Form,
  id,
  defaultValue,
  type,
}: ValueFieldProps & { type: 'example' | 'default' }) => {
  const { t } = useScopedTranslate(locale);

  switch (field.type) {
    case 'number':
      return (
        <Form.TextField
          id={id}
          label={field.name}
          hideLabel
          validators={['number']}
          defaultValue={(defaultValue ?? '').toString()}
        />
      );
    case 'decimal':
      return (
        <Form.TextField
          id={id}
          label={field.name}
          hideLabel
          validators={['decimal']}
          defaultValue={(defaultValue ?? '').toString()}
        />
      );
    case 'select':
      return (
        <EndpointSelectField
          id={id}
          field={field as IntegrationEndpointSelectVariable}
          form={Form}
          defaultValue={defaultValue}
          hideLabel
          parameterColumn={type}
        />
      );
    case 'date':
      return <Form.DatePicker id={id} label={field.name} hideLabel defaultValue={defaultValue} />;
    case 'email':
      return (
        <Form.TextField
          id={id}
          label={field.name}
          hideLabel
          validators={['email']}
          defaultValue={defaultValue ?? ''}
        />
      );
    case 'file':
      return type === 'default' ? (
        <MultipartDefaultFileField id={id} field={field} form={Form} />
      ) : (
        <MultipartExampleFileField id={id} field={field} form={Form} />
      );
    case 'boolean':
      return (
        <FieldMinHeightWrapper>
          <Form.Switch
            id={id}
            label={field.name}
            labelVisible={false}
            defaultValue={defaultValue ?? false}
          />
        </FieldMinHeightWrapper>
      );
    case 'array':
      return <></>;
    case 'array_of_strings': {
      const arrayField = field as IntegrationEndpointArrayOfStringsVariable;
      const hasOptionsList = Boolean(arrayField.options_from || arrayField.options?.length);
      if (hasOptionsList) {
        return (
          <EndpointSelectMultiField
            id={id}
            field={arrayField}
            form={Form}
            defaultValue={defaultValue}
            parameterColumn={type}
          />
        );
      }
      const textValue = Array.isArray(defaultValue) ? defaultValue.join('\n') : defaultValue ?? '';
      return (
        <Form.Textarea
          id={id}
          label={field.name}
          hideLabel
          defaultValue={textValue}
          style={arrayFieldTextareaStyle}
          helpText={t('oneItemPerLine')}
        />
      );
    }
    case 'secret':
      return (
        <Form.TextField
          id={id}
          label={field.name}
          hideLabel
          validators={type === 'default' && !field.has_secret_value ? ['required'] : undefined}
          placeholder={
            type === 'example'
              ? t('secretExamplePlaceholder')
              : field.has_secret_value
              ? t('secretLeaveEmpty')
              : undefined
          }
        />
      );
    case 'json':
      return <JsonValueField id={id} label={field.name} defaultValue={defaultValue} />;
    case 'paragraph':
    case 'text':
    default:
      return (
        <Form.TextField id={id} label={field.name} hideLabel defaultValue={defaultValue ?? ''} />
      );
  }
};

const toJsonText = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  return JSON.stringify(value, null, 2);
};

const JsonValueField = ({
  id,
  label,
  defaultValue,
}: {
  id: keyof Data;
  label: string;
  defaultValue: unknown;
}) => {
  const { value, onChange, onBlur, error, isDisabled } = useFormField<string>({
    id,
    label,
    defaultValue: toJsonText(defaultValue),
    validators: ['json'],
  });

  const handleBlur = (event: FocusEvent<HTMLTextAreaElement>) => {
    // Pretty-print valid JSON on blur; leave anything the json validator would
    // reject as typed so the formatter and validator agree.
    const text = event.target.value;
    try {
      const formatted = JSON.stringify(JSON.parse(text), null, 2);
      if (formatted !== text) {
        onChange(formatted);
      }
    } catch {
      // Not valid JSON — the json validator flags it on save.
    }
    onBlur(event);
  };

  return (
    <TextArea
      id={id}
      label={label}
      hideLabel
      value={value ?? ''}
      onChange={onChange}
      onBlur={handleBlur}
      error={error}
      isDisabled={isDisabled}
      style={arrayFieldTextareaStyle}
    />
  );
};

type EndpointSelectFieldProps = {
  id: keyof Data;
  field: IntegrationEndpointSelectVariable;
  form: typeof Form;
  defaultValue: any;
  hideLabel?: boolean;
  parameterColumn: 'example' | 'default';
};

// Resolves a list field's parameters from its sibling fields' example/default
// values and fetches the options — shared by the single- and multi-select fields.
const useEndpointDynamicOptions = (
  field: IntegrationEndpointListVariable,
  parameterColumn: 'example' | 'default'
) => {
  const endpoint = useContext(endpointContext);
  return useDynamicEndpointOptions(field, {
    getFieldId: (variableName) => {
      const ids = getFieldIds(variableName);
      return String(parameterColumn === 'default' ? ids.defaultValueId : ids.exampleId);
    },
    getFieldLabel: (variableName) =>
      (endpoint.variables ?? []).find((variable) => variable.name === variableName)?.label ??
      variableName,
  });
};

const EndpointSelectField = ({
  id,
  field,
  form: Form,
  defaultValue,
  hideLabel,
  parameterColumn,
}: EndpointSelectFieldProps) => {
  const { t } = useScopedTranslate(locale);
  const dynamic = useEndpointDynamicOptions(field, parameterColumn);
  if (dynamic.isDynamic && !dynamic.hasAllParameters) {
    return (
      <Form.Select
        id={id}
        label={field.name}
        hideLabel={hideLabel}
        options={[]}
        placeholder={t('dynamicSelectMissingFields', {
          fields: dynamic.missingFields.join(', '),
        })}
        isDisabled
      />
    );
  }
  return (
    <Form.Select
      id={id}
      label={field.name}
      hideLabel={hideLabel}
      options={dynamic.isDynamic ? dynamic.options : field.options ?? []}
      defaultValue={defaultValue}
    />
  );
};

type EndpointSelectMultiFieldProps = {
  id: keyof Data;
  field: IntegrationEndpointArrayOfStringsVariable;
  form: typeof Form;
  defaultValue: any;
  parameterColumn: 'example' | 'default';
};

const EndpointSelectMultiField = ({
  id,
  field,
  form: Form,
  defaultValue,
  parameterColumn,
}: EndpointSelectMultiFieldProps) => {
  const { t } = useScopedTranslate(locale);
  const dynamic = useEndpointDynamicOptions(field, parameterColumn);

  const { options, selectedOptions } = useMemo(() => {
    const selectedValues: string[] = Array.isArray(defaultValue) ? defaultValue : [];
    const baseOptions = dynamic.isDynamic ? dynamic.options : field.options ?? [];
    const selected = selectedValues.map(
      (value) => baseOptions.find((option) => option.value === value) ?? { value, label: value }
    );
    return { options: baseOptions, selectedOptions: selected };
  }, [dynamic.isDynamic, dynamic.options, field.options, defaultValue]);

  const isMissing = dynamic.isDynamic && !dynamic.hasAllParameters;
  const isLoadingList = dynamic.isDynamic && dynamic.isLoading;

  if (isMissing || isLoadingList) {
    return (
      <SelectMulti
        options={options}
        value={selectedOptions}
        onChange={() => undefined}
        isDisabled
        label=""
        placeholder={
          isMissing
            ? t('dynamicSelectMissingFields', { fields: dynamic.missingFields.join(', ') })
            : t('loadingList')
        }
        menuPortalTarget={document.body}
      />
    );
  }

  return (
    <Form.SelectMulti
      id={id}
      label=""
      options={options}
      defaultValue={selectedOptions}
      menuPortalTarget={document.body}
    />
  );
};

const arrayFieldTextareaStyle = {
  minHeight: pxToRem(38),
  padding: `${pxToRem(8)} ${pxToRem(12)}`,
  boxSizing: 'border-box',
  fieldSizing: 'content',
} as const;

const locale = {
  en: {
    oneItemPerLine: 'One item per line',
    secretLeaveEmpty: 'Leave empty to keep current',
    secretExamplePlaceholder: 'Leave empty to use stored credential',
    dynamicSelectMissingFields: 'Fill in {fields} first',
    loadingList: 'Loading…',
  },
};
